import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Shell } from '../../components/layout/Shell';
import { api, API_BASE } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { FitBadge, ScoreBars, ScoreRadar } from '../../components/hikari/ScoreBreakdown';
import { toast } from 'sonner';
import {
  ArrowLeft, User, ShieldCheck, GraduationCap, Briefcase, FileText, LinkSimple, Sparkle, MapPin,
  EnvelopeSimple, Phone, CalendarBlank, CheckCircle, Warning, Target, Lightbulb, ChartBar,
} from '@phosphor-icons/react';
import ProjectMedia from '../../components/hikari/ProjectMedia';

// Hiring pipeline stages, in order. Status in DB uses these exact strings.
const STAGES = [
  { key: 'applied', label: 'Applied' },
  { key: 'invite', label: 'Invited' },
  { key: 'interviewed', label: 'Interviewed' },
  { key: 'offered', label: 'Offered' },
  { key: 'hire', label: 'Hired' },
];

const stageIndex = (k) => Math.max(0, STAGES.findIndex((s) => s.key === k));

const SIGNAL_STYLE = {
  skill: 'bg-primary text-primary-foreground',
  specialization: 'bg-foreground text-background',
  location: 'bg-accent text-foreground',
  work_auth: 'bg-secondary text-foreground border border-border',
};

const EmployerCandidateProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const jobId = searchParams.get('job');

  const [profile, setProfile] = useState(null);
  const [scores, setScores] = useState([]);
  const [files, setFiles] = useState([]);
  const [application, setApplication] = useState(null);
  const [job, setJob] = useState(null);
  const [why, setWhy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const loadApp = async () => {
    if (!jobId) return;
    try {
      const r = await api.get(`/employer/applications/${id}/${jobId}`);
      setApplication(r.data);
      if (r.data?.job) setJob(r.data.job);
    } catch {
      setApplication(null);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const whyUrl = jobId
          ? `/employer/candidate/${id}/why?job_id=${encodeURIComponent(jobId)}`
          : `/employer/candidate/${id}/why`;
        const [p, s, f, w] = await Promise.all([
          api.get(`/candidates/${id}`),
          api.get(`/scoring/candidate/${id}/all`).catch(() => ({ data: [] })),
          api.get(`/files/user/${id}`).catch(() => ({ data: [] })),
          api.get(whyUrl).catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        setProfile(p.data);
        setScores(s.data || []);
        setFiles(f.data || []);
        setWhy(w.data || null);
        if (jobId) {
          try {
            const a = await api.get(`/employer/applications/${id}/${jobId}`);
            if (!cancelled) {
              setApplication(a.data);
              setJob(a.data?.job || null);
            }
          } catch { /* no app yet */ }
        }
      } catch (e) {
        if (e?.response?.status === 404) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [id, jobId]);

  const token = typeof window !== 'undefined' ? localStorage.getItem('hikari_token') : '';
  const fileUrl = (fileId) => `${API_BASE}/files/download/${fileId}?auth=${encodeURIComponent(token || '')}`;

  const advance = async (nextKey) => {
    try {
      await api.post(`/employer/applications/${id}/${jobId}/action`, { action: nextKey });
      toast.success(`Moved to ${STAGES.find((s) => s.key === nextKey)?.label || nextKey}`);
      await loadApp();
    } catch {
      toast.error('Could not update stage');
    }
  };

  const currentIdx = useMemo(
    () => application ? stageIndex(application.status) : -1,
    [application]
  );

  const currentScore = useMemo(() => {
    if (why?.score) return why.score;
    if (jobId) return scores.find((s) => s.job_id === jobId) || scores[0];
    return scores[0];
  }, [scores, jobId, why]);

  if (notFound) {
    return (
      <Shell>
        <div className="max-w-[800px] mx-auto px-6 py-20 text-center" data-testid="candidate-not-found">
          <div className="label-eyebrow text-primary mb-2">404</div>
          <h1 className="font-display font-black text-4xl tracking-tighter mb-4">Candidate not found</h1>
          <Button className="rounded-none" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft size={16} className="mr-2" /> Back
          </Button>
        </div>
      </Shell>
    );
  }

  if (loading || !profile) {
    return <Shell><div className="p-8 mono text-sm">Loading profile...</div></Shell>;
  }

  const contactEmail = profile.email || '';
  const phone = profile.phone || '';
  const jobTitle = application?.job?.title || why?.job?.title || '';
  const mailtoHref = contactEmail
    ? `mailto:${contactEmail}?subject=${encodeURIComponent(jobTitle ? `Interview — ${jobTitle}` : 'Hikari · Next step')}&body=${encodeURIComponent(`Hi ${profile.full_name || ''},\n\nWe reviewed your Hikari profile and would love to schedule a short interview${jobTitle ? ` for the ${jobTitle} role` : ''}.\n\nAre you available this week?\n\n— `)}`
    : '';

  return (
    <Shell>
      <div className="max-w-[1400px] mx-auto px-6 py-10">
        <button
          onClick={() => navigate(-1)}
          className="mono text-xs text-muted-foreground hover:text-foreground mb-6 flex items-center gap-1"
          data-testid="back-link"
        >
          <ArrowLeft size={14} /> BACK
        </button>

        {/* Pipeline bar — only visible when employer opened from a specific job */}
        {jobId && application && (
          <div className="bg-foreground text-background p-6 mb-0 border border-border" data-testid="pipeline-bar">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div>
                <div className="label-eyebrow text-accent mb-1">HIRING PIPELINE</div>
                <div className="font-display font-black text-xl tracking-tight">
                  {application.job?.title || 'Role'}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {contactEmail && (
                  <a href={mailtoHref} data-testid="pipeline-email">
                    <Button size="sm" className="rounded-none bg-background text-foreground hover:bg-background/90">
                      <EnvelopeSimple size={14} className="mr-1" /> Email
                    </Button>
                  </a>
                )}
                {phone && (
                  <a href={`tel:${phone}`} data-testid="pipeline-call">
                    <Button size="sm" variant="outline" className="rounded-none bg-transparent border-background text-background hover:bg-background hover:text-foreground">
                      <Phone size={14} className="mr-1" /> Call
                    </Button>
                  </a>
                )}
              </div>
            </div>

            {/* Stage rail */}
            <ol className="grid grid-cols-5 gap-1" data-testid="pipeline-stages">
              {STAGES.map((st, i) => {
                const active = i === currentIdx;
                const done = i < currentIdx;
                return (
                  <li key={st.key} className={`p-3 border ${active ? 'bg-accent text-foreground border-accent' : done ? 'border-accent/50 text-accent' : 'border-background/20 text-background/60'}`}>
                    <div className="flex items-center gap-1 label-eyebrow">
                      {done && <CheckCircle size={12} weight="fill" />}
                      <span className="mono">{String(i + 1).padStart(2, '0')}</span>
                    </div>
                    <div className="mt-1 font-display font-black text-sm">{st.label}</div>
                  </li>
                );
              })}
            </ol>

            {/* Advance buttons */}
            <div className="mt-5 flex flex-wrap gap-2" data-testid="pipeline-actions">
              {STAGES.slice(1).map((st, i) => {
                const isNext = i + 1 === currentIdx + 1;
                const disabled = i + 1 <= currentIdx;
                return (
                  <Button
                    key={st.key}
                    size="sm"
                    onClick={() => advance(st.key)}
                    disabled={disabled}
                    variant={isNext ? 'default' : 'outline'}
                    className={`rounded-none ${isNext ? 'bg-accent text-foreground hover:bg-accent/90' : 'bg-transparent border-background/40 text-background hover:bg-background hover:text-foreground'} disabled:opacity-30`}
                    data-testid={`pipeline-advance-${st.key}`}
                  >
                    {disabled ? `✓ ${st.label}` : `Mark as ${st.label}`}
                  </Button>
                );
              })}
            </div>

            {/* Timeline */}
            {application.timeline?.length > 0 && (
              <div className="mt-6 border-t border-background/20 pt-4" data-testid="pipeline-timeline">
                <div className="label-eyebrow text-accent mb-2">TIMELINE</div>
                <ul className="space-y-1 mono text-xs">
                  {application.timeline.slice(-8).reverse().map((t, idx) => (
                    <li key={idx} className="flex items-center gap-3 opacity-80">
                      <CalendarBlank size={12} />
                      <span className="w-32">{new Date(t.at).toLocaleString()}</span>
                      <span className="uppercase font-bold">{t.action}</span>
                      {t.note && <span className="italic opacity-70">— {t.note}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* ---------- WHY THIS CANDIDATE — hero panel ---------- */}
        {why && currentScore && (
          <section
            data-testid="why-panel"
            className="bg-white border border-border p-8 mb-0"
          >
            <div className="flex items-start gap-6 flex-wrap">
              <FitBadge value={currentScore.overall_fit} size="lg" />
              <div className="flex-1 min-w-[260px]">
                <div className="label-eyebrow text-primary mb-2 flex items-center gap-2">
                  <Sparkle size={14} weight="fill" /> WHY THIS CANDIDATE
                </div>
                <h1
                  className="font-display font-black text-3xl lg:text-4xl tracking-tighter leading-tight mb-2"
                  data-testid="why-headline"
                >
                  {profile.full_name || why.candidate?.name}
                  {jobTitle && (
                    <span className="block text-muted-foreground text-lg font-semibold mt-1">
                      for {jobTitle}
                    </span>
                  )}
                </h1>
                <p className="text-sm text-muted-foreground mono" data-testid="why-tldr">
                  {why.tldr}
                </p>
              </div>
              <div className="mono text-xs text-muted-foreground text-right">
                <div>{currentScore.attempts_considered} attempts considered</div>
                {currentScore.computed_at && (
                  <div className="mt-1">
                    scored {new Date(currentScore.computed_at).toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>

            {/* Explanation quote */}
            {currentScore.explanation && (
              <blockquote
                className="mt-6 border-l-4 border-primary pl-4 py-2 text-base leading-relaxed"
                data-testid="why-explanation"
              >
                {currentScore.explanation}
              </blockquote>
            )}

            {/* Radar + Bars side-by-side */}
            <div className="grid md:grid-cols-2 gap-8 mt-8 pt-6 border-t border-border">
              <div>
                <div className="label-eyebrow text-muted-foreground mb-3 flex items-center gap-1">
                  <ChartBar size={12} /> SCORE BREAKDOWN
                </div>
                <ScoreRadar scores={currentScore} />
              </div>
              <div>
                <div className="label-eyebrow text-muted-foreground mb-3">COMPONENT SCORES</div>
                <ScoreBars scores={currentScore} />
                {currentScore.weights_used && (
                  <p className="mono text-xs text-muted-foreground mt-3">
                    Weighted for this role:{' '}
                    {Object.entries(currentScore.weights_used)
                      .map(([k, v]) => `${k} ${Math.round(v * 100)}%`)
                      .join(' · ')}
                  </p>
                )}
              </div>
            </div>

            {/* Matched signals + gaps */}
            <div className="grid md:grid-cols-2 gap-8 mt-8 pt-6 border-t border-border">
              <div data-testid="why-matched">
                <div className="label-eyebrow text-muted-foreground mb-3 flex items-center gap-1">
                  <Target size={12} /> MATCHED SIGNALS · {why.matched_signals.length}
                </div>
                {why.matched_signals.length === 0 ? (
                  <div className="mono text-xs text-muted-foreground">
                    No direct signal matches detected.
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {why.matched_signals.map((sig, i) => (
                      <span
                        key={`${sig.kind}-${sig.label}-${i}`}
                        className={`label-eyebrow px-2 py-1 ${SIGNAL_STYLE[sig.kind] || 'bg-secondary text-foreground'}`}
                        data-testid={`match-${sig.kind}`}
                        title={sig.kind}
                      >
                        {sig.label}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div data-testid="why-gaps">
                <div className="label-eyebrow text-muted-foreground mb-3 flex items-center gap-1">
                  <Warning size={12} /> GAPS TO PROBE · {why.missing_skills.length}
                </div>
                {why.missing_skills.length === 0 ? (
                  <div className="mono text-xs text-muted-foreground">
                    Covers every required skill listed on the job.
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {why.missing_skills.map((s) => (
                      <span
                        key={s}
                        className="label-eyebrow px-2 py-1 bg-muted text-muted-foreground border border-dashed border-border"
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Proof points */}
            {why.proof_points.length > 0 && (
              <div className="mt-8 pt-6 border-t border-border" data-testid="why-proof">
                <div className="label-eyebrow text-muted-foreground mb-3 flex items-center gap-1">
                  <Lightbulb size={12} /> PROOF POINTS · TOP {why.proof_points.length}
                </div>
                <div className="grid md:grid-cols-3 gap-3">
                  {why.proof_points.map((p) => (
                    <div
                      key={p.id}
                      className="border border-border bg-secondary/30 overflow-hidden"
                      data-testid={`proof-${p.id}`}
                    >
                      <ProjectMedia project={p} aspect="16/9" />
                      <div className="p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="font-medium text-sm leading-tight">{p.title}</div>
                          {p.url && (
                            <a
                              href={p.url}
                              target="_blank"
                              rel="noreferrer"
                              className="label-eyebrow text-primary hover:underline shrink-0"
                            >
                              <LinkSimple size={12} />
                            </a>
                          )}
                        </div>
                        {p.description && (
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-3">
                            {p.description}
                          </p>
                        )}
                        {p.matched_skills?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {p.matched_skills.map((s) => (
                              <span
                                key={s}
                                className="label-eyebrow bg-primary/10 text-primary px-1.5 py-0.5"
                              >
                                {s}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Work-style */}
            {currentScore.work_style_summary && (
              <div className="mt-8 pt-6 border-t border-border" data-testid="why-workstyle">
                <div className="label-eyebrow text-muted-foreground mb-2">WORK-STYLE</div>
                <p className="text-sm italic leading-relaxed">{currentScore.work_style_summary}</p>
              </div>
            )}

            {/* Red flags */}
            {why.red_flags?.length > 0 && (
              <div className="mt-8 pt-6 border-t border-border" data-testid="why-flags">
                <div className="label-eyebrow text-destructive mb-2 flex items-center gap-1">
                  <Warning size={12} weight="fill" /> INTEGRITY FLAGS · {why.red_flags.length}
                </div>
                <div className="flex flex-wrap gap-2">
                  {why.red_flags.slice(0, 8).map((f, i) => (
                    <span
                      key={i}
                      className="label-eyebrow bg-destructive/10 text-destructive px-2 py-1 border border-destructive/30"
                    >
                      {f.source} · {f.label}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Attempts breakdown */}
            {why.attempts_breakdown?.length > 0 && (
              <div className="mt-8 pt-6 border-t border-border" data-testid="why-attempts">
                <div className="label-eyebrow text-muted-foreground mb-3">
                  ASSESSMENTS CONSIDERED · {why.attempts_breakdown.length}
                </div>
                <ul className="divide-y divide-border border border-border">
                  {why.attempts_breakdown.map((a) => (
                    <li key={a.id} className="p-2 flex items-center gap-4 mono text-xs">
                      <span className="label-eyebrow w-24">{(a.type || 'attempt').toUpperCase()}</span>
                      <span className="font-bold w-12">{a.score ?? '—'}</span>
                      <span className="text-muted-foreground w-24">
                        {a.duration_sec ? `${Math.round(a.duration_sec / 60)} min` : '—'}
                      </span>
                      {a.flags?.length > 0 && (
                        <span className="text-destructive">
                          ⚠ {a.flags.length} flag{a.flags.length > 1 ? 's' : ''}
                        </span>
                      )}
                      <span className="ml-auto text-muted-foreground">
                        {a.submitted_at ? new Date(a.submitted_at).toLocaleDateString() : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        )}

        {/* ---------- Lower half: identity + long-form evidence ---------- */}
        <div className="grid lg:grid-cols-12 gap-0 grid-skeleton mt-0">
          {/* Left: identity + facts */}
          <div className="lg:col-span-4 bg-white p-8" data-testid="candidate-identity">
            <div className="flex items-center gap-2 label-eyebrow text-muted-foreground mb-3">
              <User size={14} /> CANDIDATE
            </div>
            <h2 className="font-display font-black text-2xl tracking-tighter mb-1">
              {profile.full_name || 'Unnamed'}
            </h2>
            <div className="text-sm text-muted-foreground mb-4">
              {profile.headline || '—'}
            </div>

            <div className="flex flex-wrap gap-2 mb-6">
              {contactEmail && (
                <a href={mailtoHref} data-testid="contact-email">
                  <Button size="sm" variant="outline" className="rounded-none">
                    <EnvelopeSimple size={14} className="mr-1" /> Email
                  </Button>
                </a>
              )}
              {phone && (
                <a href={`tel:${phone}`} data-testid="contact-call">
                  <Button size="sm" variant="outline" className="rounded-none">
                    <Phone size={14} className="mr-1" /> Call
                  </Button>
                </a>
              )}
            </div>

            <dl className="space-y-2 mono text-sm border-t border-border pt-4">
              {contactEmail && (
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Email</dt>
                  <dd className="font-medium truncate max-w-[200px]" title={contactEmail}>{contactEmail}</dd>
                </div>
              )}
              {phone && (
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Phone</dt>
                  <dd className="font-medium">{phone}</dd>
                </div>
              )}
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Location</dt>
                <dd className="font-medium flex items-center gap-1">
                  <MapPin size={12} /> {profile.location || '—'}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Country</dt>
                <dd className="font-medium">{profile.country || '—'}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Work auth</dt>
                <dd className="font-medium">{profile.work_authorization || '—'}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Availability</dt>
                <dd className="font-medium">{profile.availability || '—'}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Credentials</dt>
                <dd className="font-medium">{profile.credentials?.length || 0}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Projects</dt>
                <dd className="font-medium">{profile.projects?.length || 0}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Files</dt>
                <dd className="font-medium">{files.length}</dd>
              </div>
            </dl>
          </div>

          {/* Right: evidence */}
          <div className="lg:col-span-8 bg-white p-8" data-testid="candidate-evidence">
            <section className="mb-10">
              <div className="flex items-center gap-2 mb-4">
                <FileText size={18} className="text-primary" />
                <div className="label-eyebrow">RESUME &amp; WORK SAMPLES · {files.length}</div>
              </div>
              {files.length === 0 ? (
                <div className="mono text-xs text-muted-foreground">No files uploaded.</div>
              ) : (
                <ul className="divide-y divide-border border border-border" data-testid="candidate-files">
                  {files.map((f) => (
                    <li key={f.id} className="p-3 flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{f.original_filename || '(file)'}</div>
                        <div className="mono text-xs text-muted-foreground">
                          {(f.purpose || 'file').toUpperCase()} · {Math.round((f.size || 0) / 1024)} KB
                        </div>
                      </div>
                      <a
                        href={fileUrl(f.id)}
                        target="_blank"
                        rel="noreferrer"
                        className="label-eyebrow text-primary hover:underline shrink-0"
                        data-testid={`file-open-${f.id}`}
                      >
                        OPEN ↗
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* Candidate's answers to the employer's job-specific custom questions */}
            {jobId && job?.custom_questions?.length > 0 && (
              <section className="mb-10" data-testid="candidate-custom-answers">
                <div className="flex items-center gap-2 mb-4">
                  <FileText size={18} className="text-primary" />
                  <div className="label-eyebrow">JOB-SPECIFIC ANSWERS · {job.custom_questions.length}</div>
                </div>
                <ul className="space-y-3">
                  {job.custom_questions.map((q, idx) => {
                    const a = application?.answers?.[q.id];
                    return (
                      <li key={q.id} className="border border-border p-4" data-testid={`answer-row-${idx}`}>
                        <div className="label-eyebrow text-muted-foreground mb-1">Q{idx + 1}</div>
                        <div className="font-medium text-sm mb-2">{q.prompt}</div>
                        {a ? (
                          <p className="text-sm leading-relaxed whitespace-pre-wrap bg-secondary/40 p-2 border-l-2 border-primary">{a}</p>
                        ) : (
                          <p className="mono text-xs text-muted-foreground">No answer recorded.</p>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}

            <section className="mb-10">
              <div className="flex items-center gap-2 mb-4">
                <GraduationCap size={18} className="text-primary" />
                <div className="label-eyebrow">CREDENTIALS · {profile.credentials?.length || 0}</div>
              </div>
              {(profile.credentials || []).length === 0 ? (
                <div className="mono text-xs text-muted-foreground">None listed.</div>
              ) : (
                <ul className="space-y-2" data-testid="candidate-credentials">
                  {profile.credentials.map((c) => (
                    <li key={c.id} className="border border-border p-3 text-sm">
                      <div className="font-medium">{c.title}</div>
                      <div className="mono text-xs text-muted-foreground">
                        {c.issuer || '—'} · {c.year || '—'} {c.verified && '· VERIFIED'}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="mb-10">
              <div className="flex items-center gap-2 mb-4">
                <Briefcase size={18} className="text-primary" />
                <div className="label-eyebrow">PROJECTS · {profile.projects?.length || 0}</div>
              </div>
              {(profile.projects || []).length === 0 ? (
                <div className="mono text-xs text-muted-foreground">None listed.</div>
              ) : (
                <ul className="grid sm:grid-cols-2 gap-3" data-testid="candidate-projects">
                  {profile.projects.map((p) => (
                    <li key={p.id} className="border border-border bg-white overflow-hidden">
                      <ProjectMedia project={p} aspect="16/9" />
                      <div className="p-4">
                        <div className="flex items-center justify-between mb-1">
                          <div className="font-medium text-sm">{p.title}</div>
                          {p.url && (
                            <a href={p.url} target="_blank" rel="noreferrer" className="label-eyebrow text-primary hover:underline flex items-center gap-1">
                              <LinkSimple size={12} /> OPEN
                            </a>
                          )}
                        </div>
                        {p.description && <p className="text-sm text-muted-foreground mb-2 line-clamp-3">{p.description}</p>}
                        <div className="flex flex-wrap gap-1">
                          {(p.skills || []).map((s) => (
                            <span key={s} className="label-eyebrow bg-secondary px-2 py-0.5">{s}</span>
                          ))}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section>
              <div className="flex items-center gap-2 mb-4">
                <ShieldCheck size={18} className="text-primary" />
                <div className="label-eyebrow">FIT ACROSS YOUR JOBS · {scores.length}</div>
              </div>
              {scores.length === 0 ? (
                <div className="mono text-xs text-muted-foreground">No scores computed yet.</div>
              ) : (
                <ul className="divide-y divide-border border border-border" data-testid="candidate-scores">
                  {scores.map((s) => (
                    <li key={s.id} className="p-3 flex items-center gap-4">
                      <FitBadge value={s.overall_fit} size="sm" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{s.job?.title || '(unknown job)'}</div>
                        <div className="mono text-xs text-muted-foreground">
                          {(s.job?.source || 'manual').toUpperCase()} · {s.job?.location || '—'}
                        </div>
                      </div>
                      <Link
                        to={`/employer/jobs/${s.job_id}`}
                        className="label-eyebrow text-primary hover:underline shrink-0"
                      >
                        OPEN JOB
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        </div>
      </div>
    </Shell>
  );
};

export default EmployerCandidateProfile;
